import { type Ref, computed, ref } from 'vue'
import type { ChatMemberResponse } from '../services/chatApiService'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import type { IChatMatrixParticipant } from '@/modules/chat-matrix-fast/types/chatEmployee'
import { treeItemToChatParticipant } from '@/modules/chat-matrix-fast/utils/employeeTree'

interface UseParticipantSelectionOptions {
  currentMembers: Ref<ChatMemberResponse[]>
}

function collectMemberKeys(member: ChatMemberResponse): string[] {
  const keys = [String(member.user.id)]
  if (member.user_uuid) {
    keys.push(member.user_uuid)
  }
  return keys.filter(Boolean)
}

function collectParticipantKeys(participant: IChatMatrixParticipant): string[] {
  return [participant.id, participant.matrix_user_id].filter(Boolean)
}

export function collectMembersExcludeKeys(members: ChatMemberResponse[]): Set<string> {
  return new Set(members.flatMap(collectMemberKeys))
}

function isParticipantInMembers(
  participant: IChatMatrixParticipant,
  members: ChatMemberResponse[],
): boolean {
  const existingKeys = collectMembersExcludeKeys(members)
  return collectParticipantKeys(participant).some(key => existingKeys.has(key))
}

/** Управление списком новых участников для добавления в группу */
export function useParticipantSelection(options: UseParticipantSelectionOptions) {
  const { currentMembers } = options

  const selectedUsers = ref<IChatMatrixParticipant[]>([])

  const selectedItems = computed(() =>
    selectedUsers.value.map(user => ({
      id: user.id,
      name: user.full_name,
      description: user.position_name,
    })),
  )

  function onEmployeesSelected(employees: IEmployeeTreeItem[]) {
    const newParticipants = employees
      .map(treeItemToChatParticipant)
      .filter((participant): participant is IChatMatrixParticipant => Boolean(participant))
      .filter(participant => !isParticipantInMembers(participant, currentMembers.value))
      .filter(participant => !selectedUsers.value.some(user => user.id === participant.id))

    selectedUsers.value.push(...newParticipants)
  }

  function removeUser(userId: string | number) {
    selectedUsers.value = selectedUsers.value.filter(user => user.id !== String(userId))
  }

  function getNewUserIds(): string[] {
    return selectedUsers.value
      .map(user => user.id)
      .filter(Boolean)
  }

  function reset() {
    selectedUsers.value = []
  }

  return {
    selectedUsers,
    selectedItems,
    onEmployeesSelected,
    removeUser,
    getNewUserIds,
    reset,
    isParticipantInMembers,
  }
}
